<template>
  <input
    type="text"
    inputmode="numeric"
    :value="displayValue"
    @input="onInput"
    @blur="onBlur"
    @focus="onFocus"
    @keydown="onKeyDown"
    @focusin="$emit('focusin')"
    @focusout="$emit('focusout')"
  />
</template>

<script>
export default {
  name: 'InputFormatter',
  props: {
    value: {
      type: String,
      default: ''
    },
    format: {
      type: String,
      required: true // 예: "###-###", "##:##"
    },
    minTime: {
      type: String,
      default: '00:00'
    },
    maxTime: {
      type: String,
      default: '23:59'
    },
  },
  data() {
    return {
      rawValue: '',
      placeholder: '_',
    };
  },
  computed: {
    displayValue() {
      return this.applyFormat(this.rawValue);
    }
  },
  watch: {
    value(val) {
      this.rawValue = this.extractValidChars(val);
    }
  },
  mounted() {
    this.rawValue = this.extractValidChars(this.value);
  },
  methods: {
    onInput(e) {
      const input = e.target.value;
      const stripped = this.extractValidChars(input);
      let cursorPosition = e.target.selectionStart;

      // 백스페이스로 삭제할 때는 마지막 숫자만 제거
      if (input.length < this.displayValue.length) {
        this.rawValue = stripped
      } else {
        if (e.target.value[cursorPosition] === ':') {
          cursorPosition += 1;
        }
        // 시간 포맷 검증
        if (this.isTimeFormat()) {
          const newValue = this.validateTimeInput(stripped);
          if (newValue !== stripped) {
            this.rawValue = newValue;
            e.target.value = this.applyFormat(newValue);
            return;
          }
        }
        this.rawValue = stripped;
      }

      // 포맷 적용 및 이벤트 발생
      const formatted = this.applyFormat(this.rawValue);
      this.$emit('input', formatted);
      e.target.value = formatted;

      // 커서 위치 조정
      this.$nextTick(() => {
        e.target.setSelectionRange(cursorPosition, cursorPosition);
      });
    },
    onBlur() {
      const formatted = this.applyFormat(this.rawValue);
      let finalValue = formatted.replace(/_/g, '0'); // placeholder 제거

      // minTime과 maxTime 범위 체크
      if (this.isTimeFormat() && finalValue) {
        let [hours, minutes] = finalValue.split(':').map(Number);

        // 시간과 분의 유효성 검사
        if (minutes > 59) {
          minutes = 0;
        }
        if (hours > 23) {
          hours = 23;
        }

        const totalMinutes = hours * 60 + minutes;

        const [minHours, minMinutes] = (this.minTime || '00:00').split(':').map(Number);
        const [maxHours, maxMinutes] = (this.maxTime || '23:59').split(':').map(Number);
        const minTotalMinutes = minHours * 60 + minMinutes;
        const maxTotalMinutes = maxHours * 60 + maxMinutes;

        // minTime이 maxTime보다 큰 경우 (자정을 걸치는 경우)
        if (minTotalMinutes > maxTotalMinutes) {
          if (totalMinutes >= minTotalMinutes || totalMinutes <= maxTotalMinutes) {
            // 유효한 범위 내에 있음
            finalValue = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
          } else {
            // 유효하지 않은 범위인 경우 minTime으로 조정
            const adjustedHours = Math.floor(minTotalMinutes / 60);
            const adjustedMinutes = minTotalMinutes % 60;
            finalValue = `${adjustedHours.toString().padStart(2, '0')}:${adjustedMinutes.toString().padStart(2, '0')}`;
          }
        } else {
          // 일반적인 경우 (minTime이 maxTime보다 작은 경우)
          if (totalMinutes < minTotalMinutes) {
            const adjustedHours = Math.floor(minTotalMinutes / 60);
            const adjustedMinutes = minTotalMinutes % 60;
            finalValue = `${adjustedHours.toString().padStart(2, '0')}:${adjustedMinutes.toString().padStart(2, '0')}`;
          } else if (totalMinutes > maxTotalMinutes) {
            const adjustedHours = Math.floor(maxTotalMinutes / 60);
            const adjustedMinutes = maxTotalMinutes % 60;
            finalValue = `${adjustedHours.toString().padStart(2, '0')}:${adjustedMinutes.toString().padStart(2, '0')}`;
          } else {
            finalValue = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
          }
        }
      }
      this.$emit('input', !this.rawValue ? this.rawValue : finalValue);
    },
    onFocus() {
      // 선택 전체 잡기 등 필요한 UX가 있다면 여기에 추가
    },
    onKeyDown(e) {
      if (!this.isTimeFormat()) return;

      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const currentTime = this.rawValue.padStart(4, '0');
        const hours = parseInt(currentTime.slice(0, 2));
        const minutes = parseInt(currentTime.slice(2, 4));

        // minTime과 maxTime을 분 단위로 변환
        const [minHours, minMinutes] = this.minTime.split(':').map(Number);
        const [maxHours, maxMinutes] = this.maxTime.split(':').map(Number);
        const minTotalMinutes = minHours * 60 + minMinutes;
        const maxTotalMinutes = maxHours * 60 + maxMinutes;

        let totalMinutes = hours * 60 + minutes;
        if (e.key === 'ArrowUp') {
          totalMinutes += 5;
        } else {
          totalMinutes -= 5;
        }

        // minTime과 maxTime 범위 내로 조정
        if (totalMinutes > maxTotalMinutes) {
          totalMinutes = minTotalMinutes;
        } else if (totalMinutes < minTotalMinutes) {
          totalMinutes = maxTotalMinutes;
        }

        const newHours = Math.floor(totalMinutes / 60);
        const newMinutes = totalMinutes % 60;

        this.rawValue = `${newHours.toString().padStart(2, '0')}${newMinutes.toString().padStart(2, '0')}`;
        const formatted = this.applyFormat(this.rawValue);
        this.$emit('input', formatted);
        e.target.value = formatted;
      }
    },
    isValidChar(char) {
      return /^\d$/.test(char);
    },
    extractValidChars(input) {
      if (!input) return '';
      // 숫자만 추출
      return input.replace(/[^\d]/g, '');
    },
    isTimeFormat() {
      return /^H{2}:m{2}(:s{2})?$/.test(this.format);
    },
    validateTimeInput(value) {
      const parts = this.format.split(':');
      let validatedValue = '';
      let currentIndex = 0;

      // minTime과 maxTime을 분 단위로 변환
      const [minHours, minMinutes] = this.minTime.split(':').map(Number);
      const [maxHours, maxMinutes] = this.maxTime.split(':').map(Number);
      const minTotalMinutes = minHours * 60 + minMinutes;
      const maxTotalMinutes = maxHours * 60 + maxMinutes;

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const maxLength = part.length;
        const currentPart = value.slice(currentIndex, currentIndex + maxLength);

        if (currentPart.length === 0) break;

        let validatedPart = currentPart;
        const numValue = parseInt(currentPart);

        if (i === 0) { // HH
          if (numValue > 23) {
            validatedPart = '23';
          }
        } else { // mm or ss
          if (numValue > 59) {
            validatedPart = '59';
          }
        }

        validatedValue += validatedPart;
        currentIndex += currentPart.length;
      }

      // 전체 시간이 minTime과 maxTime 범위를 벗어나는지 확인
      if (validatedValue.length >= 4) {
        const hours = parseInt(validatedValue.slice(0, 2));
        const minutes = parseInt(validatedValue.slice(2, 4));
        const totalMinutes = hours * 60 + minutes;

        // minTime이 maxTime보다 큰 경우 (자정을 걸치는 경우)
        if (minTotalMinutes > maxTotalMinutes) {
          if (totalMinutes < minTotalMinutes && totalMinutes > maxTotalMinutes) {
            // 유효하지 않은 범위인 경우 minTime으로 조정
            const adjustedHours = Math.floor(minTotalMinutes / 60);
            const adjustedMinutes = minTotalMinutes % 60;
            validatedValue = `${adjustedHours.toString().padStart(2, '0')}${adjustedMinutes.toString().padStart(2, '0')}`;
          }
        } else {
          // 일반적인 경우
          if (totalMinutes < minTotalMinutes) {
            const adjustedHours = Math.floor(minTotalMinutes / 60);
            const adjustedMinutes = minTotalMinutes % 60;
            validatedValue = `${adjustedHours.toString().padStart(2, '0')}${adjustedMinutes.toString().padStart(2, '0')}`;
          } else if (totalMinutes > maxTotalMinutes) {
            const adjustedHours = Math.floor(maxTotalMinutes / 60);
            const adjustedMinutes = maxTotalMinutes % 60;
            validatedValue = `${adjustedHours.toString().padStart(2, '0')}${adjustedMinutes.toString().padStart(2, '0')}`;
          }
        }
      }

      return validatedValue;
    },
    applyFormat(raw) {
      let formatted = '';
      let rawIndex = 0;
      const formatLength = this.format.replace(/[^#Hms]/g, '').length;

      // 입력값을 포맷 길이로 제한
      raw = raw.slice(0, formatLength);

      for (let i = 0; i < this.format.length; i++) {
        const formatChar = this.format[i];

        if (formatChar === '#' || formatChar === 'H' || formatChar === 'm' || formatChar === 's') {
          if (rawIndex < raw.length) {
            formatted += raw[rawIndex];
            rawIndex++;
          } else {
            formatted += this.placeholder;
          }
        } else {
          formatted += formatChar;
        }
      }

      return formatted;
    }
  }
};
</script>

<style scoped>
input {
  color: #3c3c3c;
  letter-spacing: 1px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  font-size: 1rem;
  min-height: 37.59px;
}
</style>
